//v1.0.0 - Drag controller to
import { globals } from 'config/globals';
import { Vector2 } from 'app/contracts/Geometry';
import Config from 'config/settings';
import { AxisType } from 'app/enum/Geometry';
import { Events } from 'core/events/Events';
import { device } from 'utils/device';

// ——————————————————————————————————————————————————
// ProgressTimeline.js
// ——————————————————————————————————————————————————

export interface Point {
	x: number;
	y: number;
}

export class InertiaDrag {
	public disableOnRelease: boolean = true;
	public friction: number = 0.96;
	public axis: string = 'both';
	public simulate: boolean = false;
	public simulateSteps: number = 60;
	public stepAfterRelease: boolean = true;
	public onPress: Function = null;
	public onStep: Function = null;
	public onRelease: Function = null;
	public releaseOutside: Function = null;
	public eventTag: string;
	public eventType: string;

	private _active: boolean = false;
	private _stopFlag: boolean = false;
	private _stopFlagAllowMouseUp: boolean;
	private mouse: Point;
	private previous: Point;
	private position: Point;
	private velocity: Point;
	private dragging: boolean;
	private $container: any;
	private _allowDrag: boolean = true;
	private _velocityXFrames: Array<any> = [];
	//window.scrollY captured at drag start — written but never read (public so
	//noUnusedLocals doesn't flag it):
	public _startScroll: number;

	private _fireStep: boolean;
	private _sensitivityMulitplier: number = 8;

	public static readonly STEP: string = 'STEP';

	//Store window/body based event id so they can created / removed dynamically
	private _eventIDs: any = {
		mouseMove: 0,
		mouseUp: 0,
		touchEnd: 0
	};

	public get isDragging(): boolean {
		return this.dragging;
	}

	constructor($target: Element | HTMLElement | Window) {
		this._active = false;
		this.$container = $target || window;
		this.position = { x: 0, y: 0 };
		this.previous = { x: 0, y: 0 };
		this.mouse = { x: 0, y: 0 };
		this.velocity = { x: 0, y: 0 };
		this.dragging = false;
		this._addEvents();
		this._step();
	}

	public destroy(): void {
		this._active = false;
		this.$container.removeEventListener('pointerdown', this._handlePointerDown.bind(this), { passive: true });
		this._removeWindowBindedEvents();
		this.$container = null;
		console.log('InertniaDrag destroy');
	}

	public setAllowDrag(allowDrag: boolean): void {
		this._allowDrag = allowDrag;
	}

	public setActive(active: boolean): void {
		if (!this._active && active) {
			this._active = true;
		}
		this._active = active;

		if (this._active) {
			this._allowDrag = true;
		}

		this._stopFlagAllowMouseUp = false;
	}

	public tick(delta: number = 1): Vector2 {
		return this._step(delta);
	}

	public forceStop(allowMouseUp: boolean = false): void {
		this.dragging = this._active = false;
		this._stopFlag = true;
		this._stopFlagAllowMouseUp = allowMouseUp;
	}

	public removeVelocity(): void {
		this.velocity = { x: 0, y: 0 };
	}

	public removeResidule(): void {
		this.removeVelocity();
		this.dragging = false;
	}

	private _addEvents(): void {
		if (Config.allowMouse) {
			this.$container.addEventListener('pointerdown', this._handlePointerDown.bind(this), { passive: true });
		}

		if (Config.allowTouch) {
		}
	}

	_handlePointerDown(e: PointerEvent) {
		if (!this._active || !this._allowDrag) {
			return;
		}

		this.eventType = e.pointerType == 'touch' ? 'touch' : 'mouse';
		this._addWindowBindedEvents();
		this._stopFlag = false;
		this.position.x = this.previous.x = this.mouse.x = e.clientX;
		this.position.y = this.previous.y = this.mouse.y = e.clientY;
		this.dragging = true;
		this._startScroll = window.scrollY;

		this._velocityXFrames = [];

		if (this.onPress) {
			this.onPress();
		}
	}

	private _handleMouseCancel(_event): void {
		if (!this.dragging) return;
		this._onPointerUp(true);
	}

	private _handleMouseMove(event): void {
		if (!this.dragging || !this._allowDrag) return;
		const blockUpdate: boolean =
			this.eventType === 'touch' &&
			this.axis === 'x' &&
			(globals.mouseController.moveAxis === AxisType.VERTICAL ||
				globals.mouseController.moveAxis === null);
		event.preventDefault();

		if (!this._active || this._stopFlag) {
			return;
		}

		if (!blockUpdate) {
			this.mouse.x = event.clientX;
			this.mouse.y = event.clientY;
		}

		if (
			event.clientY < 3 ||
			event.clientY > device.innerHeight - 3 ||
			event.clientX < 3 ||
			event.clientX > device.innerWidth - 3
		) {
			this._onPointerUp();
		}
	}

	private _addWindowBindedEvents(): void {
		//MOUSE MOVE
		//----------------------------------------------------------------------

		if (!this._eventIDs.pointerCancel) {
			this._eventIDs.pointerCancel = Events.on('pointercancel', window, () => this._handleMouseCancel(true));
		}

		if (!this._eventIDs.mouseMove) {
			this._eventIDs.mouseMove = Events.on('pointermove', window, this._handleMouseMove.bind(this));
		}

		if (!this._eventIDs.mouseUp) {
			this._eventIDs.mouseUp = Events.on('pointerup', window, () => this._onPointerUp(false));
		}
	}

	private _onPointerUp(fromCancel: boolean = false): void {
		if (!this.dragging) return;

		this.dragging = false;

		//IMPORTANT - DON@T FORGET TO CLEAR THE DYANMIC EVENTS (AS they will build up)

		if ((!this._active || this._stopFlag) && !this._stopFlagAllowMouseUp) {
			return;
		}

		//Block touch x dragging when not in correct QUAD
		const blockUpdate: boolean =
			this.eventType === 'touch' &&
			this.axis === 'x' &&
			(globals.mouseController.moveAxis === AxisType.VERTICAL ||
				globals.mouseController.moveAxis === null);

		if (!blockUpdate) {
			let simulatedSteps: Point;

			if (!fromCancel) {
				simulatedSteps = this._simulateSteps();
			} else {
				simulatedSteps = { x: 0, y: 0 };
			}

			if (this.onRelease) {
				this.onRelease(simulatedSteps);
			}

			if (this.disableOnRelease) {
				this._active = false;
			}
		} else {
			this.onRelease({ x: 0, y: 0 });
		}
	}

	private _removeWindowBindedEvents(): void {
		Events.offById(
			this._eventIDs.mouseUp,
			this._eventIDs.touchEnd,
			this._eventIDs.mouseMove,
			this._eventIDs.documentLeave,
			this._eventIDs.pointerCancel
		);

		this._eventIDs.mouseUp = null;
		this._eventIDs.touchEnd = null;
		this._eventIDs.mouseMove = null;
		this._eventIDs.documentLeave = null;
		this._eventIDs.pointerCancel = null;
	}

	private _step(delta: number = 1): Vector2 {
		if (!this._active || this._stopFlag) {
			return null;
		}

		if (this.dragging) {
			this.previous.x = this.position.x;
			this.previous.y = this.position.y;

			this.position.x = this.mouse.x;
			this.position.y = this.mouse.y;

			this.velocity.x = this.position.x - this.previous.x;
			this.velocity.y = this.position.y - this.previous.y;

			this._velocityXFrames.push(this.velocity.x);
			if (this._velocityXFrames.length >= 10) {
				this._velocityXFrames.unshift();
			}
		} else {
			this.position.x += this.velocity.x * delta;
			this.position.y += this.velocity.y * delta;

			this.velocity.x *= this.friction * delta;
			this.velocity.y *= this.friction * delta;
		}

		this._fireStep = true;

		if (this.velocity.x === 0 && this.velocity.y === 0) {
			this._fireStep = false;
		} else if (!this.dragging && !this.stepAfterRelease) {
			this._fireStep = false;
		}

		if (this._fireStep) {
			if (this.onStep) {
				this.onStep({
					x: this.velocity.x,
					y: this.velocity.y
				});
			}

			return {
				x: this.velocity.x,
				y: this.velocity.y
			};
		}

		return null;
	}

	//After the user has released mouse simulates release to see where it would get
	private _simulateSteps(): Point {
		if (!this.simulate || this.simulateSteps === 0) {
			return { x: 0, y: 0 };
		}

		let tempPosition: Point = { x: 0, y: 0 };

		const xAvg = this._velocityXFrames.length > 0 ? this._getVelocityAvg(this._velocityXFrames) : 1;
		const tempVelocity: Point = { x: xAvg, y: this.velocity.y };

		tempPosition = { x: 0, y: 0 };

		if (Math.abs(xAvg) > 4) {
			for (let i = 0; i < this.simulateSteps; i++) {
				tempPosition.x += tempVelocity.x * this._sensitivityMulitplier;
				tempPosition.y += tempVelocity.y;
				tempVelocity.x *= this.friction;
				tempVelocity.y *= this.friction;
			}
		}

		this._velocityXFrames = [];

		return tempPosition;
	}

	// Note: reads this._velocityXFrames directly; the param is ignored (sole
	// caller passes that same array, so behavior is unaffected).
	private _getVelocityAvg(_frames: Array<number>): number {
		let count = 0;
		let total = 0;

		for (let i = 0; i < this._velocityXFrames.length; i++) {
			if (this._velocityXFrames[i] != 0) {
				count++;
				total += this._velocityXFrames[i];
			}
		}

		if (count == 0 || total == 0) {
			return 0;
		}

		return total / count;
	}
}
